import React, { useState, useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Button, Col, Container, Row, Form } from "react-bootstrap";
import RoutesListTable from "./RoutesListTable";
import PaginationComponent from "../../PaginationComponent";
import "../../../assets/styles/dashboard.scss";
import api from "../../../services/httpClient";

const RoutesList = () => {
  const [selectedOption, setSelectedOption] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [filters, setFilters] = useState({});
  const [appliedFilters, setAppliedFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: "Id", direction: "asc" });
  const [fetchVersion, setFetchVersion] = useState(0);

  const [data, setData] = useState({
    items: [],
    totalRecords: 0,
    totalPages: 0,
    pageNumber: 1,
    pageSize: itemsPerPage,
  });

  const currentPageRef = useRef(currentPage);
  const itemsPerPageRef = useRef(itemsPerPage);
  const appliedFiltersRef = useRef(appliedFilters);
  const sortConfigRef = useRef(sortConfig);

  currentPageRef.current = currentPage;
  itemsPerPageRef.current = itemsPerPage;
  appliedFiltersRef.current = appliedFilters;
  sortConfigRef.current = sortConfig;
  const formatPostcode = (value) => {
    const digits = String(value ?? "")
      .replace(/\D/g, "")
      .slice(0, 5);
    return digits.length > 2
      ? `${digits.slice(0, 2)}-${digits.slice(2)}`
      : digits;
  };
  const handleOptionChange = (event) => {
    setSelectedOption(event.target.value);
    setFilters({
      terminalEx: "",
      terminalDr: "",
      city: "",
      postcode: "",
      street: "",
    });
    setAppliedFilters({
      terminalEx: "",
      terminalDr: "",
      city: "",
      postcode: "",
      street: "",
    });
  };

  const lastFetchedVersionRef = useRef(0);

  React.useEffect(() => {
    if (fetchVersion === 0) return; // Nie wykonuj zapytania, gdy fetchVersion = 0
    if (lastFetchedVersionRef.current === fetchVersion) return;
    lastFetchedVersionRef.current = fetchVersion;

    let cancelled = false;
    const pageNumber = currentPageRef.current;
    const pageSize = itemsPerPageRef.current;
    const filtersToApply = appliedFiltersRef.current;
    const sort = sortConfigRef.current;

    (async () => {
      try {
        const response = await api.get(`/Address/List`, {
          params: {
            pageNumber,
            pageSize,
            SortBy: sort.key,
            SortDirection: sort.direction,
            ...filtersToApply,
          },
        });

        if (cancelled) return;
        setData({
          items: Array.isArray(response.data.items) ? response.data.items : [],
          totalRecords: response.data.totalRecords || 0,
          totalPages: response.data.totalPages || 0,
          pageNumber: response.data.pageNumber || 1,
          pageSize: response.data.pageSize || pageSize,
        });
      } catch (error) {
        if (cancelled) return;
        console.error("Error fetching data:", error);
        setData({
          items: [],
          totalRecords: 0,
          totalPages: 0,
          pageNumber: 1,
          pageSize: pageSize,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchVersion]);

  const handleFilterChange = (field, value) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      [field]: value,
    }));
  };

  const handleSearch = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    setTimeout(() => setFetchVersion((v) => v + 1), 0);
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    if (fetchVersion > 0) {
      // Tylko, jeśli już wyszukano
      setTimeout(() => setFetchVersion((v) => v + 1), 0);
    }
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (fetchVersion > 0) {
      // Tylko, jeśli już wyszukano
      setTimeout(() => setFetchVersion((v) => v + 1), 0);
    }
  };

  const handleItemsPerPageChange = (pageSize) => {
    setItemsPerPage(pageSize);
    setCurrentPage(1);
    if (fetchVersion > 0) {
      // Tylko, jeśli już wyszukano
      setTimeout(() => setFetchVersion((v) => v + 1), 0);
    }
  };

  return (
    <div className="routes-list-container">
      <Container fluid>
        <Row className="align-items-center mb-4">
          <h4>Lista tras</h4>
        </Row>
        <Form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
        >
          <Row className="mb-4">
            <Col>
              <Form.Group controlId="option">
                <Form.Control
                  as="select"
                  value={selectedOption}
                  onChange={handleOptionChange}
                >
                  <option value="">-- Wybierz opcje --</option>
                  <option value="terminal">Terminal</option>
                  <option value="cityAndPostcode">
                    Miejscowość + Kod pocztowy
                  </option>
                  <option value="postcode">Kod pocztowy</option>
                  <option value="cityAndStreet">Miejscowość + Ulica</option>
                </Form.Control>
              </Form.Group>
            </Col>
            {selectedOption && (
              <Col md="auto">
                <Row className="mb-3">
                {selectedOption === "terminal" && (
                  <>
                    <Col>
                      <Form.Group controlId="terminalEx">
                        <Form.Control
                          type="text"
                          placeholder="Wprowadź Terminal EX"
                          value={filters.terminalEx}
                          onChange={(e) =>
                            handleFilterChange("terminalEx", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="terminalDr">
                        <Form.Control
                          type="text"
                          placeholder="Wprowadź Terminal DR"
                          value={filters.terminalDr}
                          onChange={(e) =>
                            handleFilterChange("terminalDr", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                {selectedOption === "cityAndPostcode" && (
                  <>
                    <Col>
                      <Form.Group controlId="cityInput">
                        <Form.Control
                          type="text"
                          placeholder="Wprowadź Miejscowość"
                          value={filters.city}
                          onChange={(e) =>
                            handleFilterChange("city", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="postcodeInput">
                        <Form.Control
                          type="text"
                          placeholder="__-___"
                          inputMode="numeric"
                          value={filters.postcode || ""}
                          onChange={(e) =>
                            handleFilterChange(
                              "postcode",
                              formatPostcode(e.target.value),
                            )
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                {selectedOption === "postcode" && (
                  <Col>
                    <Form.Group controlId="postcodeOnlyInput">
                      <Form.Control
                        type="text"
                        placeholder="Wprowadź Kod pocztowy"
                        value={filters.postcode}
                        onChange={(e) =>
                          handleFilterChange("postcode", e.target.value)
                        }
                      />
                    </Form.Group>
                  </Col>
                )}
                {selectedOption === "cityAndStreet" && (
                  <>
                    <Col>
                      <Form.Group controlId="cityInput">
                        <Form.Control
                          type="text"
                          placeholder="Wprowadź Miejscowość"
                          value={filters.city}
                          onChange={(e) =>
                            handleFilterChange("city", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                    <Col>
                      <Form.Group controlId="streetInput">
                        <Form.Control
                          type="text"
                          placeholder="Wprowadź Ulicę"
                          value={filters.street}
                          onChange={(e) =>
                            handleFilterChange("street", e.target.value)
                          }
                        />
                      </Form.Group>
                    </Col>
                  </>
                )}
                </Row>
              </Col>
            )}
            <Col className={selectedOption ? "" : "d-flex align-items-center"}>
              <div className="d-flex">
                <Button
                  variant="secondary"
                  className="dashboard-optional-button me-2"
                  type="button"
                  onClick={() => {
                    setFilters({
                      terminalEx: "",
                      terminalDr: "",
                      city: "",
                      postcode: "",
                      street: "",
                    });
                    setAppliedFilters({
                      terminalEx: "",
                      terminalDr: "",
                      city: "",
                      postcode: "",
                      street: "",
                    });
                  }}
                >
                  Wyczyść
                </Button>
                <Button
                  variant="primary"
                  className="dashboard-only-button"
                  type="submit"
                  onClick={handleSearch}
                >
                  Szukaj
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
        <Row className="mb-4">
          {selectedOption && (
            <RoutesListTable
              data={data}
              onSortChange={handleSortChange}
              selectedOption={selectedOption}
              filters={appliedFilters}
            />
          )}
        </Row>
        <Row className="align-items-center mt-4">
          <PaginationComponent
            itemsPerPage={data.pageSize}
            dataCount={data.items.length}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            itemsPerPageOptions={[20, 100, 500]}
          />
        </Row>
      </Container>
    </div>
  );
};

export default RoutesList;
